import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatPrice, CATEGORY_LABELS } from "@/lib/api";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const [hovered, setHovered] = useState(false);

  const quickAdd = (e) => {
    e.preventDefault();
    const size = product.sizes[Math.floor(product.sizes.length / 2)] || "M";
    addItem(product, size, 1);
    toast.success(`${product.name} ajouté au panier`, { description: `Taille ${size} — modifiable dans le panier` });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/produit/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group block border border-white/10 bg-coal hover:border-white/25 transition-colors"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hovered && product.hover_image ? "opacity-0" : "opacity-100"}`}
          />
          {product.hover_image && (
            <img
              src={product.hover_image}
              alt=""
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hovered ? "opacity-100" : "opacity-0"}`}
            />
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.is_new && (
              <span className="bg-volt text-ink font-mono text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1">Nouveau</span>
            )}
            {product.authentic && (
              <span data-testid={`authentic-badge-${product.id}`} className="inline-flex items-center gap-1 bg-white text-ink font-mono text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1">
                <ShieldCheck size={11} /> 100% Authentique
              </span>
            )}
            {product.stock <= 5 && (
              <span className="bg-blood text-white font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1">
                Plus que {product.stock}
              </span>
            )}
          </div>
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={quickAdd}
            aria-label="Ajout rapide"
            className="absolute bottom-3 right-3 bg-white text-ink p-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-blood hover:text-white"
          >
            <Plus size={18} />
          </button>
        </div>
        <div className="p-4 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash mb-1">
              {CATEGORY_LABELS[product.category] || product.category}
            </p>
            <h3 className="font-syne font-bold uppercase tracking-tight text-sm leading-snug">{product.name}</h3>
          </div>
          <span className="font-mono text-sm text-volt whitespace-nowrap">{formatPrice(product.price)}</span>
        </div>
      </Link>
    </motion.div>
  );
};
